<template>
  <fragment>
    <div
      v-if="xAxisCategories.length"
      class="graph-container"
    >
      <div class="sac__font-10 f-white med">
        Last 14 days
      </div>
      <highcharts
        class="area-chart"
        :options="chartOptions"
      />
      <div class="start-label">
        {{ minLabel }}
      </div>
      <div class="end-label">
        {{ maxLabel }}
      </div>
    </div>
    <div v-else class="no-graph-container">
      Graph data not available
    </div>
  </fragment>
</template>

<script>
import moment from 'moment';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import convertToCelsiusResult from '@shared/lib/converter/convertToCelsiusResult';
import serviceMixin from '@shared/mixins/serviceMixin';

export default {
  name: 'AreaGraph',
  mixins: [serviceMixin, unitPreferencesMixin],
  props: {
    alertDetail: Object,
  },
  data() {
    return {
      minIndex: 0,
      maxIndex: 0,
    };
  },
  computed: {
    performanceData() {
      const {
        tempFormat,
        alertDetail: {
          graphData,
          alertCycleList,
        },
      } = this;
      return graphData.map((d) => {
        const [key] = Object.keys(d);
        const cycleMatches = alertCycleList.find((c) => c && moment(c.startTime).isSame(key));
        const y = tempFormat === '°C' ? convertToCelsiusResult(d[key]) : d[key];
        return {
          y,
          marker: {
            enabled: !!cycleMatches,
            radius: 6,
            fillColor: '#FFFFFF',
            lineWidth: 3,
            lineColor: '#F83356',
          },
        };
      });
    },
    healthyMinMax() {
      const {
        alert: {
          healthyMin,
          healthyMax,
        },
      } = this.alertDetail;
      const maxY = Math.max(...this.performanceData.map((x) => x.y));
      const min = this.tempFormat === '°C' ? convertToCelsiusResult(healthyMin) : healthyMin;
      const max = this.tempFormat === '°C' ? convertToCelsiusResult(healthyMax) : healthyMax;
      return {
        min,
        max,
        maxY: max > maxY ? max + 4 : maxY + 4,
      };
    },
    xAxisCategories() {
      return this.alertDetail.graphData.map((d) => Object.keys(d)[0]);
    },
    minLabel() {
      if (this.dateFormat === 'M/D') {
        return moment(this.xAxisCategories[this.minIndex]).format('MMM Do h:mmA');
      }
      return moment(this.xAxisCategories[this.minIndex]).format('Do MMM h:mmA');
    },
    maxLabel() {
      if (this.dateFormat === 'M/D') {
        return moment(this.xAxisCategories[this.maxIndex]).format('MMM Do h:mmA');
      }
      return moment(this.xAxisCategories[this.maxIndex]).format('Do MMM h:mmA');
    },
    chartOptions() {
      const {
        tempFormat,
        dateFormat,
      } = this;
      return {
        title: {
          text: '',
        },
        chart: {
          type: 'area',
          zoomType: 'x',
          width: null,
          height: 200,
          spacingBottom: 10,
          spacingLeft: 40,
          spacingRight: 20,
          spacingTop: 10,
          animation: false,
          backgroundColor: 'transparent',
        },
        credits: {
          enabled: false,
        },
        tooltip: {
          useHTML: true,
          nullFormat: 'Value is not available.',
          formatter() {
            return `<div class="sac__font-10">${moment(this.x).format(`${dateFormat === 'M/D' ? 'MMM Do h:mmA' : 'Do MMM h:mmA'}`)}<br/>`
              + `<b>${this.y.toFixed(0)}${tempFormat}</b></div>`;
          },
        },
        yAxis: {
          opposite: true,
          gridLineDashStyle: 'dash',
          gridLineWidth: 0.2,
          gridLineColor: '#2B3757',
          labels: {
            enabled: true,
            style: {
              fontSize: 12,
              color: 'white',
              fontWeight: 'bold',
              fontFamily: 'Gilroy, sans-serif',
            },
          },
          min: 0,
          max: this.healthyMinMax.maxY,
          plotLines: [
            {
              color: '#FFFFFF',
              dashStyle: 'dot',
              width: 1,
              value: this.healthyMinMax.min,
              zIndex: 5,
              label: {
                text: `${this.healthyMinMax.min}${tempFormat}`,
                rotation: -90,
                style: {
                  fontSize: 10,
                  color: 'white',
                  fontWeight: 'bold',
                  fontFamily: 'Gilroy, sans-serif',
                },
                y: 10,
                x: -10,
              },
            },
            {
              color: '#FFFFFF',
              dashStyle: 'dot',
              width: 1,
              value: this.healthyMinMax.max,
              zIndex: 5,
              label: {
                text: `${this.healthyMinMax.max}${tempFormat}`,
                rotation: -90,
                style: {
                  fontSize: 10,
                  color: 'white',
                  fontWeight: 'bold',
                  fontFamily: 'Gilroy, sans-serif',
                },
                y: 10,
                x: -10,
              },
            },
          ],
          title: {
            enabled: false,
          },
        },
        xAxis: {
          categories: this.xAxisCategories,
          labels: {
            enabled: false,
          },
          events: {
            setExtremes: this.onSetExtremes,
          },
        },
        plotOptions: {
          series: {
            label: {
              connectorAllowed: true,
            },
            connectNulls: true,
            animation: {
              duration: 0,
            },
          },
        },
        series: [{
          name: '',
          showInLegend: false,
          color: '#232734',
          data: this.performanceData,
          lineWidth: 3,
          lineColor: '#5A637D',
          dataLabels: {
            formatter() {
              return null;
            },
            style: {
              color: '#F3F4F6',
              fontFamily: 'Gilroy, sans-serif',
            },
            borderRadius: 16,
            enabled: true,
            useHTML: true,
            y: -5,
          },
        }],
        responsive: {
          rules: [{
            condition: {
              maxWidth: 2000,
            },
          }],
        },
      };
    },
  },
  mounted() {
    if (this.xAxisCategories.length) {
      this.maxIndex = this.xAxisCategories.length - 1;
    }
  },
  methods: {
    onSetExtremes(event) {
      const { min, max } = event;
      if (min || max) {
        this.minIndex = Math.round(min);
        this.maxIndex = Math.round(max);
      } else {
        this.minIndex = 0;
        this.maxIndex = this.xAxisCategories.length - 1;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.graph-container {
  position: relative;
  .start-label {
    position: absolute;
    color: white;
    top: 210px;
    left: 30px;
    font-size: 10px;
    font-weight: 500;
  }
  .end-label {
    position: absolute;
    color: white;
    top: 210px;
    right: 40px;
    font-size: 10px;
    font-weight: 500;
  }
}
.no-graph-container {
  display: grid;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: $N50;
  border: 2px solid $N70;
}
</style>
